import React from 'react';
import { Button, Typography, makeStyles, Box } from '@material-ui/core';
import _get from 'lodash/get';
import _classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getImageFile, replacePlaceholders } from 'utils/stringUtils';
import { getSeriesTakenInSession } from 'containers/Series/selectors';
import Badge from 'components/Badge';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Image from 'components/ResourceItemSmall/components/Image';
import ResourceItemTitle from 'components/ResourceItemSmall/components/Title';
import { ArrowRight } from './ArrowRight';
import { CheckIcon } from './CheckIcon';

const useStyles = makeStyles(theme => ({
  link: {
    display: 'flex',
    flexDirection: 'column',
    height: 271,
    borderRadius: 5,
    overflow: 'hidden',
    position: 'relative',
    color: props =>
      props.isOnResourcePage || props.style !== 'Wide Card'
        ? theme.palette.primary.main
        : theme.palette.text.white,
    border: props => props.style !== 'Wide Card' && '1px solid #E9ECED',
    backgroundColor: props => props.style !== 'Wide Card' && '#fff',
  },
  countBadge: {
    display: 'inline-flex',
    inset: '15px auto auto 15px',
    position: 'absolute',
    borderRadius: '16px',
    backgroundColor: '#ffffff',
    '& span': {
      ...theme.typography.pxSmall,
      padding: '4px 8px',
      color: '#232221',
    },
  },
  mainBackground: {
    position: 'relative',
    background: props =>
      `center top 0% / cover no-repeat url(${props.backgroundImage})`,
    height: '100%',
  },
  container: {
    marginTop: 'auto',
    position: 'relative',
    overflow: 'hidden',

    '&:before': {
      content: "''",
      position: 'absolute',
      filter: 'blur(10px)',
      inset: '0px',
      marginTop: -12,
      background: `center bottom 51%  / cover no-repeat`,
      backgroundImage: props => `url(${props.backgroundImage})`,
    },
  },
  badgeContainer: {
    color: props => props.style === 'Wide Card' && 'inherit',
    height: 'auto',
    '& span': {
      color: 'inherit',
      paddingTop: 0,
    },
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    padding: props =>
      props.isOnResourcePage ? '2px 9px 12px 14px' : '10px 20px 55px',
  },
  resourcePageContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginRight: 5,
  },
  icon: {
    width: 17,
    height: 17,
  },
  truncate: {
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  plainCardBody: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '12px',
  },
  plainCardRightContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  completedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 5px',
    borderRadius: '10px',
    fontSize: '0.75rem',
    color: theme.palette.text.white,
    backgroundColor: '#37BB7C',
  },
  plainCardTitle: {
    fontSize: '1.125rem',
    lineHeight: '1.5rem',
    textAlign: 'center',
  },
  plainCardResourcesCount: {
    textAlign: 'center',
    ...theme.typography.pxSmall,
  },
  imageContainer: {
    width: 80,
    height: 80,
    margin: '8px auto',
    borderRadius: 10,
    overflow: 'hidden',

    '& > img': {
      maxWidth: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  plainCardButton: {
    alignSelf: 'center',
    margin: 'auto 0px 20px',
    borderRadius: 5,
    fontSize: '0.75rem',
    fontWeight: 700,
    lineHeight: '1.125rem',
  },
  progress: {
    height: 6,
    flexShrink: 0,
    backgroundColor: '#ECF4F8',

    '&:before': {
      content: "''",
      display: 'block',
      height: '100%',
      width: props => `${props.progress}%`,
      backgroundColor: '#37BB7C',
    },
  },
  wideCardActionButton: {
    position: 'absolute',
    bottom: 15,
    right: 15,
    padding: '10px 16px',
    borderRadius: 8,
    color: theme.palette.text.white,
    textTransform: 'uppercase',

    '& .MuiButton-label span': {
      marginRight: 10,
    },
  },
  wideCardContinue: {
    backgroundColor: 'rgba(0, 0, 0, 0.70)',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.55)',
    },
  },
  wideCardCompleted: {
    backgroundColor: '#37BB7C',

    '&:hover': {
      backgroundColor: '#37BB7C',
    },
  },
  imageWrapper: {
    height: 123,
  },
  timeLabel: {
    color: props =>
      props.style === 'Wide Card'
        ? theme.palette.text.white
        : theme.palette.primary.main,
    ...theme.typography.pxSmall,
  },
}));

export const SeriesItem = ({
  item,
  onClick,
  route = '/series',
  trackingPath = 'series',
}) => {
  const {
    title: _title,
    name,
    resources,
    style,
    slug,
    id,
    resourcesCount,
    time,
  } = item;

  const title = _title || name;
  const backgroundImage = getImageFile(item);
  const takenSeries = useSelector(getSeriesTakenInSession);
  const [siteCopy, resourceItemPageSiteCopy] = useSiteCopySelector([
    'series',
    'resource-item-page',
  ]);

  const taken = takenSeries[id];
  const resourcesLength = resourcesCount || _get(resources, 'length', 0);
  const isCompleted = taken && taken.completed;
  const completedResourcesCount = isCompleted
    ? resourcesLength
    : _get(taken, 'completedResourcesIds.length');
  const progress = completedResourcesCount
    ? Math.round((completedResourcesCount / resourcesLength) * 100)
    : 0;
  const isStartedAndNotCompleted = progress > 0 && progress < 100;
  const isOnResourcePage =
    window.location.pathname.split('/')[1] === 'resources';

  const classes = useStyles({
    backgroundImage,
    style,
    progress,
    isOnResourcePage,
  });

  const countBadge = !!resourcesLength && (
    <div className={classes.countBadge}>
      <span>
        {replacePlaceholders(_get(siteCopy, 'pageCopy.resourcesCount'), {
          '#count': resourcesLength,
        })}
      </span>
    </div>
  );

  const timeLabel = !isCompleted && !!time && (
    <Typography className={classes.timeLabel}>
      {time}{' '}
      {time > 1
        ? _get(resourceItemPageSiteCopy, 'pageCopy.durationLabelShortPlural')
        : _get(resourceItemPageSiteCopy, 'pageCopy.durationLabelShortSingular')}
    </Typography>
  );

  const to = {
    pathname: `${route}/${slug}`,
    state: {
      trackingProps: {
        path: trackingPath,
      },
    },
  };

  if (isOnResourcePage) {
    return (
      <Link to={to} onClick={onClick} className={classes.link}>
        <div className={classes.progress} />
        <div className={classes.imageWrapper}>
          <Image file={backgroundImage} type="series" />
          {countBadge}
        </div>
        <div className={classes.infoContainer}>
          <div className={classes.resourcePageContainer}>
            <Badge removeMargin type="series" />
            {timeLabel}
          </div>
          <ResourceItemTitle title={title} />
        </div>
      </Link>
    );
  }

  const isWideCard = style === 'Wide Card';

  return isWideCard ? (
    <Link to={to} onClick={onClick} className={classes.link}>
      {progress > 0 && <div className={classes.progress} />}
      <div className={classes.mainBackground}>
        {countBadge}
        {isStartedAndNotCompleted && (
          <Button
            className={_classNames(
              classes.wideCardActionButton,
              classes.wideCardContinue,
            )}
            variant="text"
          >
            <span>{_get(siteCopy, 'pageCopy.continueCTA')}</span>
            <ArrowRight />
          </Button>
        )}
        {isCompleted && (
          <Button
            className={_classNames(
              classes.wideCardActionButton,
              classes.wideCardCompleted,
            )}
            variant="text"
          >
            <CheckIcon />
            <span>{_get(siteCopy, 'pageCopy.completedCTA')}</span>
          </Button>
        )}
      </div>
      <div className={classes.container}>
        <div className={classes.infoContainer}>
          <Box display="flex" justifyContent="space-between">
            <Badge
              customClasses={classes}
              removeMargin
              icon="seriesWhite"
              type="series"
            />
            {timeLabel}
          </Box>
          <Typography align="left" className={classes.truncate} variant="h5">
            {title}
          </Typography>
        </div>
      </div>
    </Link>
  ) : (
    <Link to={to} onClick={onClick} className={classes.link}>
      <div className={classes.progress} />
      <div className={classes.plainCardBody}>
        <Box display="flex" justifyContent="space-between">
          <Badge customClasses={classes} removeMargin type="series" />
          <div className={classes.plainCardRightContainer}>
            {isCompleted && (
              <span className={classes.completedBadge}>
                {_get(siteCopy, 'pageCopy.completedCTA')}
              </span>
            )}
            {timeLabel}
          </div>
        </Box>
        <div className={classes.imageContainer}>
          <img src={backgroundImage} alt="Series" />
        </div>
        <Typography
          className={_classNames(classes.plainCardTitle, classes.truncate)}
          variant="h5"
          color="textPrimary"
        >
          {title}
        </Typography>
        <Typography
          className={classes.plainCardResourcesCount}
          color="textPrimary"
        >
          {replacePlaceholders(_get(siteCopy, 'pageCopy.resourcesCount'), {
            '#count': resourcesLength,
          })}
        </Typography>
        <Button
          className={classes.plainCardButton}
          variant="contained"
          color="primary"
        >
          {isStartedAndNotCompleted
            ? _get(siteCopy, 'pageCopy.continueCTA')
            : isCompleted
            ? _get(siteCopy, 'pageCopy.viewResources')
            : _get(siteCopy, 'pageCopy.startCTA')}
        </Button>
      </div>
    </Link>
  );
};
